import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Select,
  Grid,
  Box,
  IconButton,
  FormHelperText,
  Card,
  Typography,
  TableContainer,
  Paper,
  Tooltip,
  Chip,
  InputAdornment,
} from "@material-ui/core";
import TablePagination from "@material-ui/core/TablePagination";
import { useFormik } from "formik";
import * as Yup from "yup";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import CloseIcon from "@material-ui/icons/Close";
import AddIcon from "@material-ui/icons/Add";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";
import FilterListIcon from "@material-ui/icons/FilterList";
import SchoolIcon from "@material-ui/icons/School";

import SectionService from "./Locality/Service/sectionService";
import AddClassService from "./Locality/Service/addClassService";

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    padding: theme.spacing(2),
    [theme.breakpoints.down("sm")]: { padding: theme.spacing(1) },
  },
  pageHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: "1.9rem",
    color: "#0f2d4d",
    lineHeight: 1.2,
  },
  pageSubtitle: {
    color: "#6b7280",
    fontSize: "0.9rem",
    marginTop: 2,
  },
  primaryBtn: {
    backgroundColor: "#1769d6",
    color: "#fff",
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    padding: "8px 18px",
    boxShadow: "0 4px 10px rgba(23,105,214,0.25)",
    "&:hover": { backgroundColor: "#125bbd" },
  },
  noteBanner: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderRadius: 12,
    backgroundColor: "#eef4ff",
    border: "1px solid #d6e4ff",
    marginBottom: theme.spacing(2),
  },
  noteIconWrap: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    backgroundColor: "#1769d6",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  filtersCard: {
    borderRadius: 14,
    border: "1px solid #e6eaf0",
    boxShadow: "0 2px 10px rgba(16,42,67,0.06)",
    marginBottom: theme.spacing(2),
    overflow: "hidden",
  },
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderBottom: "1px solid #eef1f5",
    backgroundColor: "#fafbfc",
  },
  filtersBody: {
    padding: theme.spacing(2),
  },
  filterLabel: {
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#46566b",
    marginBottom: 6,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  resetBtn: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    color: "#46566b",
  },
  tableCard: {
    borderRadius: 14,
    border: "1px solid #e6eaf0",
    boxShadow: "0 2px 10px rgba(16,42,67,0.06)",
    overflow: "hidden",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderBottom: "1px solid #eef1f5",
  },
  iconBtnOutlined: {
    border: "1px solid #e6eaf0",
    borderRadius: 8,
    padding: 7,
  },
  headCell: {
    backgroundColor: "#f4f6fa",
    color: "#46566b",
    fontWeight: 700,
    fontSize: "0.8rem",
    borderBottom: "1px solid #e6eaf0",
    whiteSpace: "nowrap",
  },
  classChip: {
    backgroundColor: "#eef4ff",
    color: "#1769d6",
    fontWeight: 600,
    height: 24,
  },
  sectionChip: {
    backgroundColor: "#f3efff",
    color: "#7c4dff",
    fontWeight: 600,
    height: 24,
  },
  statusActive: {
    backgroundColor: "#e6f7ed",
    color: "#1a9b54",
    fontWeight: 600,
    height: 24,
  },
  statusInactive: {
    backgroundColor: "#f4f6fa",
    color: "#9aa6b6",
    fontWeight: 600,
    height: 24,
  },
  iconAction: {
    padding: 6,
    color: "#46566b",
    "&:hover": { backgroundColor: "#f1f5fb", color: "#1769d6" },
  },
  deleteIcon: {
    padding: 6,
    color: "#46566b",
    "&:hover": { backgroundColor: "#ffebee", color: "#c62828" },
  },
  dialogPaper: {
    borderRadius: 14,
    maxWidth: 420,
    width: "95%",
  },
  dialogTitleBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 2.5),
    borderBottom: "1px solid #eef1f5",
    backgroundColor: "#fafafa",
  },
  dialogTitleText: {
    fontWeight: 700,
    fontSize: "1.15rem",
    color: "#0f2d4d",
  },
  dialogBody: {
    padding: theme.spacing(2.5),
  },
  fieldLabel: {
    display: "block",
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#46566b",
    marginBottom: 6,
  },
  fieldWrap: {
    marginBottom: theme.spacing(2),
  },
  dialogFooter: {
    padding: theme.spacing(1.5, 2.5),
    borderTop: "1px solid #eef1f5",
    backgroundColor: "#fafafa",
    gap: theme.spacing(1),
  },
  cancelBtn: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    color: "#46566b",
  },
  saveBtn: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    backgroundColor: "#1769d6",
    color: "#fff",
    boxShadow: "0 4px 10px rgba(23,105,214,0.25)",
    "&:hover": { backgroundColor: "#125bbd" },
  },
}));

const tableHeaders = ["Class", "Section Name", "Status", "Actions"];

export default function Section() {
  const classes = useStyles();

  const [sectionList, setSectionList] = useState([]);
  const [classList, setClassList] = useState([]);
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState({
    classId: "",
    sectionName: "",
    status: true,
  });
  const [noteOpen, setNoteOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterClassId, setFilterClassId] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [pg, setPg] = useState(0);
  const [rpg, setRpg] = useState(10);

  useEffect(() => {
    getClassList();
  }, []);

  const getClassList = () => {
    const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
    AddClassService.getAllAddClass(userDetails.schooleId)
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        const map = new Map();
        for (const item of list) {
          const key = String(item?.className || "").trim().toLowerCase();
          if (!key) continue;
          if (!map.has(key)) map.set(key, item);
        }
        const unique = Array.from(map.values()).sort((a, b) =>
          String(a?.className || "").localeCompare(String(b?.className || ""))
        );
        setClassList(unique);
      })
      .catch(() => setClassList([]));
  };

  const fetchFilteredSections = useCallback(() => {
    const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
    if (!userDetails.schooleId) return;

    if (filterClassId) {
      SectionService.findSectionList({ schooleId: userDetails.schooleId, classId: filterClassId })
        .then((res) => setSectionList(Array.isArray(res) ? res : []))
        .catch(() => setSectionList([]));
    } else {
      SectionService.getAllSection(userDetails.schooleId)
        .then((res) => setSectionList(Array.isArray(res) ? res : []))
        .catch(() => setSectionList([]));
    }
  }, [filterClassId]);

  useEffect(() => {
    setPg(0);
    fetchFilteredSections();
  }, [fetchFilteredSections]);

  const handleResetFilters = () => {
    setFilterClassId("");
    setFilterStatus("");
    setSearchTerm("");
    setPg(0);
  };

  const handleOpen = (reset = false) => {
    if (reset) {
      setSection({
        classId: "",
        sectionName: "",
        status: true,
      });
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const validationSchema = Yup.object().shape({
    classId: Yup.string().required("Class is required"),
    sectionName: Yup.string().required("Section Name is required"),
    status: Yup.boolean().required("Status is required"),
  });

  const editSection = (row) => {
    setSection({
      _id: row._id,
      classId: row.classId?._id || row.classId || "",
      sectionName: row.sectionName || "",
      status: row.status !== false,
    });
    setOpen(true);
  };

  const deleteSection = (row) => {
    if (!row) return;
    if (window.confirm("Are you sure you want to delete this section?")) {
      SectionService.deleteSection(row)
        .then(() => {
          fetchFilteredSections();
          alert("Section deleted successfully.");
        })
        .catch((err) => {
          alert(
            err?.response?.data?.message || "Failed to delete section. Please try again."
          );
        });
    }
  };

  const formik = useFormik({
    initialValues: section,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, { resetForm }) => {
      const userDetails = JSON.parse(localStorage.getItem("userDetail")) || {};
      const payload = {
        schooleId: userDetails.schooleId,
        classId: values.classId,
        sectionName: values.sectionName,
        status: values.status,
      };

      if (section._id) {
        payload._id = section._id;
        SectionService.updateSection(payload)
          .then(() => {
            handleClose();
            fetchFilteredSections();
            resetForm();
            alert("Section updated successfully.");
          })
          .catch((err) => {
            alert(
              err?.response?.data?.message || "Failed to update section. Please try again."
            );
          });
      } else {
        SectionService.addSection(payload)
          .then(() => {
            fetchFilteredSections();
            handleClose();
            resetForm();
            alert("Section added successfully.");
          })
          .catch((err) => {
            alert(
              err?.response?.data?.message || "Failed to add section. Please try again."
            );
          });
      }
    },
  });

  const filteredList = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return sectionList.filter((row) => {
      if (filterStatus === "active" && row.status === false) return false;
      if (filterStatus === "inactive" && row.status !== false) return false;

      if (!term) return true;
      const cls = (row.classId?.className || "").toLowerCase();
      const name = (row.sectionName || "").toLowerCase();
      return cls.includes(term) || name.includes(term);
    });
  }, [sectionList, searchTerm, filterStatus]);

  const activeFilterCount = [filterClassId, filterStatus].filter(Boolean).length;

  return (
    <Box className={classes.pageWrapper}>
      <Box className={classes.pageHeader}>
        <Box>
          <Typography className={classes.pageTitle}>Section</Typography>
          <Typography className={classes.pageSubtitle}>
            Manage class sections for your school. Sections are used when assigning students and teachers.
          </Typography>
        </Box>
        <Button
          variant="contained"
          className={classes.primaryBtn}
          startIcon={<AddIcon />}
          onClick={() => handleOpen(true)}
        >
          Add Section
        </Button>
      </Box>

      {noteOpen && (
        <Box className={classes.noteBanner}>
          <Box className={classes.noteIconWrap}>
            <InfoOutlinedIcon style={{ fontSize: 18 }} />
          </Box>
          <Box flex={1}>
            <Typography style={{ fontWeight: 600, color: "#1f3a5f", fontSize: "0.9rem" }}>
              Section management
            </Typography>
            <Typography style={{ color: "#46566b", fontSize: "0.85rem", marginTop: 2 }}>
              Create sections under each class (e.g. A, B, C). Use filters to find sections by class or status.
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setNoteOpen(false)}>
            <CloseIcon style={{ fontSize: 18, color: "#7a8aa0" }} />
          </IconButton>
        </Box>
      )}

      <Card elevation={0} className={classes.filtersCard}>
        <Box className={classes.filtersHeader}>
          <Box display="flex" alignItems="center" gridGap={8}>
            <FilterListIcon style={{ fontSize: 18, color: "#1769d6" }} />
            <Typography style={{ fontWeight: 700, color: "#0f2d4d", fontSize: "0.9rem" }}>
              Filters
            </Typography>
            {activeFilterCount > 0 && (
              <Chip
                size="small"
                label={`${activeFilterCount} active`}
                style={{ backgroundColor: "#eef4ff", color: "#1769d6", fontWeight: 600, height: 22 }}
              />
            )}
          </Box>
          <Box display="flex" alignItems="center" gridGap={8}>
            <Button size="small" className={classes.resetBtn} onClick={handleResetFilters}>
              Reset
            </Button>
          </Box>
        </Box>
        <Box className={classes.filtersBody}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography className={classes.filterLabel}>
                <SchoolIcon style={{ fontSize: 16, color: "#1769d6" }} />
                Class
              </Typography>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel id="filter-class-label">All Classes</InputLabel>
                <Select
                  labelId="filter-class-label"
                  label="All Classes"
                  value={filterClassId}
                  onChange={(e) => setFilterClassId(e.target.value)}
                >
                  <MenuItem value=""><em>All Classes</em></MenuItem>
                  {classList.map((c) => (
                    <MenuItem key={c._id} value={c._id}>{c.className}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography className={classes.filterLabel}>Status</Typography>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel id="filter-status-label">All Statuses</InputLabel>
                <Select
                  labelId="filter-status-label"
                  label="All Statuses"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value=""><em>All Statuses</em></MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </Card>

      <Card elevation={0} className={classes.tableCard}>
        <Box className={classes.toolbar}>
          <Typography style={{ fontWeight: 700, color: "#0f2d4d" }}>
            {filteredList.length} Section{filteredList.length !== 1 ? "s" : ""}
          </Typography>
          <Box display="flex" alignItems="center" gridGap={10}>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Search sections…"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPg(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ fontSize: 18, color: "#9aa6b6" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title="Refresh">
              <IconButton className={classes.iconBtnOutlined} size="small" onClick={fetchFilteredSections}>
                <RefreshIcon style={{ fontSize: 20, color: "#46566b" }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <TableContainer component={Paper} elevation={0} style={{ maxHeight: 520 }}>
          <Table stickyHeader size="small" className="dashboard-table-row-hover">
            <TableHead>
              <TableRow>
                {tableHeaders.map((key) => (
                  <TableCell key={key} className={classes.headCell}>{key}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredList.length === 0 && (
                <TableRow>
                  <TableCell colSpan={tableHeaders.length} align="center" style={{ padding: 48, color: "#7a8aa0" }}>
                    No sections found.
                  </TableCell>
                </TableRow>
              )}
              {filteredList.slice(pg * rpg, pg * rpg + rpg).map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell>
                    {row.classId?.className ? (
                      <Chip
                        size="small"
                        icon={<SchoolIcon style={{ fontSize: 14 }} />}
                        label={row.classId.className}
                        className={classes.classChip}
                      />
                    ) : "—"}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={row.sectionName || "—"} className={classes.sectionChip} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status !== false ? "Active" : "Inactive"}
                      className={row.status !== false ? classes.statusActive : classes.statusInactive}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Tooltip title="Edit">
                        <IconButton className={classes.iconAction} size="small" onClick={() => editSection(row)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton className={classes.deleteIcon} size="small" onClick={() => deleteSection(row)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredList.length}
          page={pg}
          onPageChange={(_, p) => setPg(p)}
          rowsPerPage={rpg}
          onRowsPerPageChange={(e) => { setRpg(parseInt(e.target.value, 10)); setPg(0); }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Card>

      <Dialog
        open={open}
        onClose={handleClose}
        scroll="paper"
        fullWidth
        maxWidth="xs"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <Box className={classes.dialogTitleBar}>
          <Typography className={classes.dialogTitleText}>
            {section._id ? "Edit Section" : "Add Section"}
          </Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon style={{ fontSize: 20, color: "#7a8aa0" }} />
          </IconButton>
        </Box>
        <form onSubmit={formik.handleSubmit}>
          <DialogContent className={classes.dialogBody}>
            <Box className={classes.fieldWrap}>
              <span className={classes.fieldLabel}>Class</span>
              <FormControl
                variant="outlined"
                size="small"
                fullWidth
                error={formik.touched.classId && Boolean(formik.errors.classId)}
              >
                <Select
                  id="classId"
                  name="classId"
                  displayEmpty
                  value={formik.values.classId}
                  onChange={formik.handleChange}
                >
                  <MenuItem value="">
                    <em>Select Class</em>
                  </MenuItem>
                  {classList.map((c) => (
                    <MenuItem key={c._id} value={c._id}>
                      {c.className}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.classId && formik.errors.classId && (
                  <FormHelperText>{formik.errors.classId}</FormHelperText>
                )}
              </FormControl>
            </Box>

            <Box className={classes.fieldWrap}>
              <span className={classes.fieldLabel}>Section Name</span>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                autoFocus
                id="sectionName"
                name="sectionName"
                type="text"
                value={formik.values.sectionName}
                onChange={formik.handleChange}
                error={formik.touched.sectionName && Boolean(formik.errors.sectionName)}
                helperText={formik.touched.sectionName && formik.errors.sectionName}
              />
            </Box>

            <Box>
              <span className={classes.fieldLabel}>Status</span>
              <FormControl
                variant="outlined"
                size="small"
                fullWidth
                error={formik.touched.status && Boolean(formik.errors.status)}
              >
                <Select
                  id="status"
                  name="status"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions className={classes.dialogFooter}>
            <Button onClick={handleClose} className={classes.cancelBtn}>Cancel</Button>
            <Button type="submit" variant="contained" className={classes.saveBtn}>
              {section._id ? "Update" : "Save"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
